/**
 * User Activity Tracking Service
 * Tracks page views, active sessions (heartbeat), and user actions
 */

// API Base URL - same as other parts of the app
const API_BASE = 'https://api.glowytics.com';

// Generate unique session ID for this browser session
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('glowytics_session_id');
  if (!sessionId) {
    sessionId = `sess_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('glowytics_session_id', sessionId);
  }
  return sessionId;
};

// Get current brand ID from storage
const getBrandId = () => {
  return localStorage.getItem('brandId') || sessionStorage.getItem('brandId') || null;
};

// Make authenticated tracking API call
const trackingFetch = async (endpoint, data) => {
  try {
    const response = await fetch(`${API_BASE}/api/tracking/${endpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Brand-ID': getBrandId() || '',
      },
      credentials: 'include',
      body: JSON.stringify({
        ...data,
        brandId: getBrandId(),
        sessionId: getSessionId(),
      }),
    });
    
    if (!response.ok) {
      console.warn(`Tracking ${endpoint} failed:`, response.status);
    }
    return response;
  } catch (error) {
    // Silently fail tracking - don't interrupt user experience
    console.warn(`Tracking ${endpoint} error:`, error.message);
    return null;
  }
};

/**
 * Track a page view
 * @param {string} pagePath - The current page path (e.g., "/dashboard")
 * @param {string} pageName - Optional human-readable page name
 */
export const trackPageView = async (pagePath, pageName = null) => {
  return trackingFetch('page-view', {
    pagePath,
    pageName,
  });
};

/**
 * Send a heartbeat to indicate user is still active
 * @param {string} pagePath - The current page path
 */
export const trackHeartbeat = async (pagePath) => {
  return trackingFetch('heartbeat', {
    pagePath,
  });
};

/**
 * Track a specific user action
 * @param {string} actionDetail - Description of the action (e.g., "clicked_budget_optimization")
 * @param {string} pagePath - The current page path
 */
export const trackAction = async (actionDetail, pagePath) => {
  return trackingFetch('action', {
    actionDetail,
    pagePath,
  });
};

// Heartbeat interval in milliseconds (30 seconds)
const HEARTBEAT_INTERVAL = 30 * 1000;

let heartbeatIntervalId = null;
let currentPath = null;

/**
 * Start heartbeat tracking for a page
 * @param {string} pagePath - The current page path
 */
export const startHeartbeat = (pagePath) => {
  // Stop any existing heartbeat
  stopHeartbeat();
  
  currentPath = pagePath;
  
  // Send first heartbeat immediately
  trackHeartbeat(pagePath);
  
  // Set up interval for subsequent heartbeats
  heartbeatIntervalId = setInterval(() => {
    if (document.visibilityState === 'visible') {
      trackHeartbeat(currentPath);
    }
  }, HEARTBEAT_INTERVAL);
};

/**
 * Stop heartbeat tracking
 */
export const stopHeartbeat = () => {
  if (heartbeatIntervalId) {
    clearInterval(heartbeatIntervalId);
    heartbeatIntervalId = null;
  }
};

/**
 * Update current path for heartbeat
 * @param {string} pagePath - The new page path
 */
export const updateHeartbeatPath = (pagePath) => {
  currentPath = pagePath;
};

export default {
  trackPageView,
  trackHeartbeat,
  trackAction,
  startHeartbeat,
  stopHeartbeat,
  updateHeartbeatPath,
  getSessionId,
};

